import React from 'react';
import { CheckCircle, Target, Eye, Award } from 'lucide-react';
import { aboutContent, companyInfo } from '../mock';
import { Card } from '../components/ui/card';
import { useLanguage } from '../i18n/LanguageContext';

const About = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {t('about.pageTitle')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('about.story')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="p-8 border-l-4 border-[#0693E3] hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <Target className="text-[#0693E3]" size={28} />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">{t('about.missionTitle')}</h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('about.mission')}
              </p>
            </Card>

            <Card className="p-8 border-l-4 border-[#CF2E2E] hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <Eye className="text-[#CF2E2E]" size={28} />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">{t('about.visionTitle')}</h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('about.vision')}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about.coreValuesTitle')}</h2>
            <p className="text-xl text-gray-600">{t('about.coreValuesSubtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="p-8 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                <CheckCircle className="text-[#0693E3]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('about.reliability')}</h3>
              <p className="text-gray-600">{t('about.reliabilityDesc')}</p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
                <CheckCircle className="text-[#CF2E2E]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('about.expertise')}</h3>
              <p className="text-gray-600">{t('about.expertiseDesc')}</p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                <CheckCircle className="text-[#0693E3]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('about.quality')}</h3>
              <p className="text-gray-600">{t('about.qualityDesc')}</p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
                <CheckCircle className="text-[#CF2E2E]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('about.partnership')}</h3>
              <p className="text-gray-600">{t('about.partnershipDesc')}</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about.journeyTitle')}</h2>
            <p className="text-xl text-gray-600">{t('about.journeySubtitle')}</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {aboutContent.milestones.map((milestone, index) => (
                <div key={index} className="flex items-start group">
                  <div className="flex-shrink-0 mr-6">
                    <div className="w-20 h-20 bg-[#0693E3] rounded-full flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform">
                      {milestone.year}
                    </div>
                  </div>
                  <div className="flex-1 pt-3">
                    <div className="bg-gray-50 p-6 rounded-lg hover:bg-blue-50 transition-colors">
                      <p className="text-lg text-gray-800 leading-relaxed">
                        {language === 'id' ? milestone.eventId : milestone.event}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications & Achievements */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t('about.certificationsTitle')}</h2>
            <p className="text-xl text-gray-400">{t('about.certificationsSubtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-gray-800 p-8 rounded-lg text-center hover:bg-gray-700 transition-colors">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0693E3] rounded-full mb-6">
                <Award className="text-white" size={40} />
              </div>
              <h3 className="text-2xl font-bold mb-2">{t('about.iso9001')}</h3>
              <p className="text-gray-400">{t('about.iso9001Desc')}</p>
            </div>

            <div className="bg-gray-800 p-8 rounded-lg text-center hover:bg-gray-700 transition-colors">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#CF2E2E] rounded-full mb-6">
                <Award className="text-white" size={40} />
              </div>
              <h3 className="text-2xl font-bold mb-2">{t('about.certifiedMaterials')}</h3>
              <p className="text-gray-400">{t('about.certifiedMaterialsDesc')}</p>
            </div>

            <div className="bg-gray-800 p-8 rounded-lg text-center hover:bg-gray-700 transition-colors">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0693E3] rounded-full mb-6">
                <Award className="text-white" size={40} />
              </div>
              <h3 className="text-2xl font-bold mb-2">{t('about.industryRecognition')}</h3>
              <p className="text-gray-400">{t('about.industryRecognitionDesc')}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
